import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import RecruiterLayout from "@/components/layout/RecruiterLayout";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Bookmark, Plus, Trash2, User, Pencil, Check } from "lucide-react";

export default function RecruiterWatchlists() {
  const [lists, setLists] = useState([]);
  const [active, setActive] = useState(null);
  const [detail, setDetail] = useState(null);
  const [newName, setNewName] = useState("");
  const [editing, setEditing] = useState(null);

  const loadLists = () => api.get("/recruiter/watchlists").then(({ data }) => setLists(data.items)).catch(() => {});
  useEffect(() => { loadLists(); }, []);
  useEffect(() => { if (active) api.get(`/recruiter/watchlists/${active}`).then(({ data }) => setDetail(data)).catch(() => {}); }, [active]);

  const create = async () => {
    if (!newName.trim()) return;
    try { const { data } = await api.post("/recruiter/watchlists", { name: newName }); setNewName(""); loadLists(); setActive(data.id); toast.success("List created"); }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || "Failed"); }
  };
  const rename = async (id, name) => { try { await api.patch(`/recruiter/watchlists/${id}`, { name }); setEditing(null); loadLists(); } catch { toast.error("Rename failed"); } };
  const del = async (id) => { if (!confirm("Delete this watch list?")) return; try { await api.delete(`/recruiter/watchlists/${id}`); if (active === id) { setActive(null); setDetail(null); } loadLists(); toast.success("Deleted"); } catch { toast.error("Failed"); } };
  const removePlayer = async (pid) => { try { await api.delete(`/recruiter/watchlists/${active}/players/${pid}`); setDetail((d) => ({ ...d, players: d.players.filter((x) => x.id !== pid), player_count: d.player_count - 1 })); } catch { toast.error("Failed"); } };

  return (
    <RecruiterLayout>
      <div className="p-6 lg:p-8 max-w-6xl">
        <div className="label-overline text-[#E63946] flex items-center gap-2"><Bookmark className="w-4 h-4" /> Recruiting Board</div>
        <h1 data-testid="watchlists-title" className="font-display text-4xl uppercase tracking-tighter mt-2">Watch Lists</h1>

        <div className="grid lg:grid-cols-3 gap-6 mt-6">
          <div className="space-y-3">
            <div className="flex gap-2">
              <input data-testid="new-list-input" value={newName} onChange={(e) => setNewName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && create()} placeholder="e.g. 2028 Ontario" className="flex-1 border border-slate-300 rounded-sm px-3 py-2 text-sm" />
              <button data-testid="create-list-btn" onClick={create} className="bg-[#E63946] text-white px-3 rounded-sm"><Plus className="w-4 h-4" /></button>
            </div>
            <div data-testid="watchlists-list" className="space-y-2">
              {lists.map((l) => (
                <div key={l.id} data-testid={`watchlist-${l.id}`} className={`bg-white border rounded-sm p-3 flex items-center justify-between cursor-pointer ${active === l.id ? "border-[#E63946]" : "border-slate-200"}`} onClick={() => setActive(l.id)}>
                  {editing === l.id ? (
                    <input autoFocus defaultValue={l.name} onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.key === "Enter" && rename(l.id, e.target.value)} onBlur={(e) => rename(l.id, e.target.value)} className="flex-1 border border-slate-300 rounded-sm px-2 py-1 text-sm" />
                  ) : (
                    <div><div className="font-semibold text-sm">{l.name}</div><div className="text-[11px] text-slate-400">{l.player_count} player(s)</div></div>
                  )}
                  <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                    <button onClick={() => setEditing(l.id)} className="p-1.5 text-slate-400 hover:text-[#0A192F]"><Pencil className="w-3.5 h-3.5" /></button>
                    <button data-testid={`delete-list-${l.id}`} onClick={() => del(l.id)} className="p-1.5 text-slate-400 hover:text-[#E63946]"><Trash2 className="w-3.5 h-3.5" /></button>
                  </div>
                </div>
              ))}
              {lists.length === 0 && <p className="text-sm text-slate-400">No watch lists yet. Create one to start building your board.</p>}
            </div>
          </div>

          <div className="lg:col-span-2">
            {detail ? (
              <div className="bg-white border border-slate-200 rounded-sm p-5" data-testid="watchlist-detail">
                <h2 className="font-display text-2xl uppercase tracking-tight">{detail.name}</h2>
                <p className="text-xs text-slate-400 mb-4">{detail.player_count} player(s)</p>
                <div className="space-y-2">
                  {detail.players.map((p) => (
                    <div key={p.id} className="flex items-center gap-3 border border-slate-100 rounded-sm p-2.5">
                      <div className="w-10 h-10 rounded-sm overflow-hidden bg-slate-100 shrink-0">{p.profile_photo_url ? <img src={p.profile_photo_url} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full grid place-items-center"><User className="w-4 h-4 text-slate-300" /></div>}</div>
                      <Link to={`/recruiter/players/${p.id}`} className="flex-1 min-w-0"><div className="font-semibold text-sm hover:text-[#E63946]">{p.first_name} {p.last_name}</div><div className="text-[11px] text-slate-400">{[p.primary_position, p.graduation_year, p.province].filter(Boolean).join(" · ")}{p.note && ` — ${p.note}`}</div></Link>
                      {p.cbr_overall_rating != null && <span className="font-data text-lg text-[#0A192F]">{p.cbr_overall_rating}</span>}
                      <button data-testid={`wl-remove-${p.id}`} onClick={() => removePlayer(p.id)} className="p-1.5 text-slate-400 hover:text-[#E63946]"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  ))}
                  {detail.players.length === 0 && <p className="text-sm text-slate-400">No players yet. Add players from the Search page.</p>}
                </div>
              </div>
            ) : (
              <div className="bg-white border border-dashed border-slate-300 rounded-sm p-12 text-center text-slate-400">Select a watch list to view players.</div>
            )}
          </div>
        </div>
      </div>
    </RecruiterLayout>
  );
}
